// ── IP 관리 어셈블리 ────────────────────────────────────────────────────────────
// 데이터 훅 + 액션 훅 + 섹션 컴포넌트를 조합한다. 2026-10-23 왼쪽 메뉴 페이지 → 출퇴근 관리 ☰ 'IP 관리' 창 내용으로
//   (IpManagementModal). 창을 연 자리가 범위다 — apiBase = /api/admin/ip-scope/{hq | company/:code | menu/:id}.
//   회사 화면은 그 회사 직원에게 연결된 IP 만, 통합 화면은 묶인 회사 + 공용(직원 연결 없음) IP (allowShared).
import React, { useState } from 'react';
import { useToast } from '../../../components/Toast';
import { useIpData } from './useIpData';
import { useIpActions } from './useIpActions';
import { OnlineUsersSection } from './OnlineUsersSection';
import { IpListSection } from './IpListSection';
import { IpAliasSection } from './IpAliasSection';
import { IpEditModal } from './IpEditModal';
import {
  cardStyle, inputStyle, statusRowStyle, addGridStyle,
} from './styles';
import { stdBlackBtn, stdOutlineBtn } from '../adminlayout/designStandard';
import { normalizeIp, formatIpWithAlias } from './utils';

/**
 * @param {string}  apiBase     범위 주소(예: /api/admin/ip-scope/company/DOT)
 * @param {boolean} allowShared 공용 IP 등록 가능(통합 출퇴근 관리만)
 */
export default function IpManagement({ apiBase, allowShared = false }) {
  const { showToast } = useToast();
  const [editRow, setEditRow] = useState(null);   // IP 주소 수정 창(2026-10-29)
  const [editBusy, setEditBusy] = useState(false);

  // ── 상태 + 파생 값 + fetchAll ─────────────────────────────────────────
  const data = useIpData({ showToast, apiBase });

  // ── 모든 액션 함수 ────────────────────────────────────────────────────
  const actions = useIpActions({
    showToast,
    apiBase,
    allowShared,
    fetchAll:         data.fetchAll,
    addForm:          data.addForm,
    currentIp:        data.currentIp,
    descDraftById:    data.descDraftById,
    aliasDraftByIp:   data.aliasDraftByIp,
    setBusyKey:       data.setBusyKey,
    setAddBusy:       data.setAddBusy,
    setDescBusyId:    data.setDescBusyId,
    setAliasBusyIp:   data.setAliasBusyIp,
    setAliasDeleteBusyIp: data.setAliasDeleteBusyIp,
    setAddForm:       data.setAddForm,
  });

  return (
    <div>
      {/* ── IP 상태 표시 + 연결 추가 폼 ── */}
      <div style={{ ...cardStyle, marginBottom: 12 }}>
        {/* 현재 IP 표시 + 가져오기 버튼 */}
        <div style={statusRowStyle(data.isMobile)}>
          <div style={{ minWidth: 0 }}>
            현재 IP:{' '}
            <code style={{ display: 'inline-block', maxWidth: 320, overflowWrap: 'anywhere', wordBreak: 'break-all' }}>
              {formatIpWithAlias(data.currentIp, data.aliasByIp.get(normalizeIp(data.currentIp)))}
            </code>
            {data.currentIpSource === 'public-fallback' && (
              <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: 12 }}>(공인 IP fallback)</span>
            )}
          </div>
          <button style={stdOutlineBtn} onClick={actions.fillCurrentIp}>현재 IP 가져오기</button>
        </div>

        {/* IP 연결 추가 폼 */}
        <div style={{ fontWeight: 700, marginBottom: 8 }}>IP 연결 추가</div>
        <div style={addGridStyle(data.isMobile)}>
          <input style={inputStyle} value={data.addForm.ip_address}
            onChange={(e) => data.setAddField('ip_address', e.target.value)}
            placeholder="IP 주소"
          />
          <select style={inputStyle} value={data.addForm.user_id}
            onChange={(e) => data.setAddField('user_id', e.target.value)}>
            {/* 회사 화면은 직원을 꼭 고른다 — 공용(회사 없음)은 통합 출퇴근 관리에서만 */}
            <option value="">{allowShared ? '공용(모든 직원)' : '직원 선택'}</option>
            {data.employees.map((user) => (
              <option key={user.id} value={user.id}>{user.name} ({user.user_id})</option>
            ))}
          </select>
          <input style={inputStyle} value={data.addForm.description}
            onChange={(e) => data.setAddField('description', e.target.value)}
            placeholder="설명 (예: 본사 WiFi)"
          />
          <input style={inputStyle} value={data.addForm.label}
            onChange={(e) => data.setAddField('label', e.target.value)}
            placeholder="별칭 (예: 본사)"
          />
          <button style={stdBlackBtn} disabled={data.addBusy} onClick={actions.submitAddForm}>
            {data.addBusy ? '등록 중...' : '등록'}
          </button>
        </div>
      </div>

      {/* ── 접속 중인 사람 (이 범위 회사 사람만 — 서버가 거른다) ── */}
      <OnlineUsersSection
        isMobile={data.isMobile}
        pagedOnlineUsers={data.pagedOnlineUsers}
        onlineUsers={data.onlineUsers}
        onlinePage={data.onlinePage}
        onlineTotalPages={data.onlineTotalPages}
        activeKeySet={data.activeKeySet}
        activePublicIpSet={data.activePublicIpSet}
        aliasByIp={data.aliasByIp}
        busyKey={data.busyKey}
        allowShared={allowShared}
        onPageChange={data.setOnlinePage}
        onRegisterUser={actions.addFromOnlineUser}
      />

      {/* ── IP 연결 목록 섹션 ── */}
      <IpListSection
        isMobile={data.isMobile}
        pagedList={data.pagedList}
        list={data.list}
        currentPage={data.currentPage}
        totalPages={data.totalPages}
        aliasByIp={data.aliasByIp}
        descDraftById={data.descDraftById}
        descBusyId={data.descBusyId}
        onDescChange={(id, value) => data.setDescDraftById((prev) => ({ ...prev, [id]: value }))}
        onSaveDesc={actions.saveDescription}
        onEdit={setEditRow}
        onToggle={actions.toggleConnection}
        onRemove={actions.removeConnection}
        onPageChange={data.setCurrentPage}
      />

      {/* ── IP 별칭 설정 섹션 ── */}
      <IpAliasSection
        isMobile={data.isMobile}
        uniqueAliasRows={data.uniqueAliasRows}
        aliasDraftByIp={data.aliasDraftByIp}
        aliasBusyIp={data.aliasBusyIp}
        aliasDeleteBusyIp={data.aliasDeleteBusyIp}
        onAliasChange={(ip, value) => data.setAliasDraftByIp((prev) => ({ ...prev, [ip]: value }))}
        onSaveAlias={actions.saveAlias}
        onRemoveAlias={actions.removeAlias}
      />

      {/* ── IP 연결 수정 창(2026-10-29) ── */}
      <IpEditModal
        row={editRow}
        currentIp={data.currentIp}
        aliasByIp={data.aliasByIp}
        busy={editBusy}
        showToast={showToast}
        onClose={() => setEditRow(null)}
        onSave={async (row, form) => {
          setEditBusy(true);
          const ok = await actions.changeIp(row, form);
          setEditBusy(false);
          if (ok) setEditRow(null);
        }}
      />
    </div>
  );
}
